import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft, Download, Send, ShieldAlert, Lock, FileSpreadsheet, FileDown,
  CheckCircle2, AlertCircle, Clock, RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format, humanizeStatus } from "@/lib/brand-tokens";
import {
  paymentRuns, paymentItems, getPaymentRunById, getMemberById, getDealById,
  getBrandById, getAdminById, ADM10_DETAIL_RUN, markPaymentRunDecrypted, markPaymentRunSent,
  markPaymentRunCompleted,
  markPaymentItemStatus,
  markPaymentItemReturned, markPaymentItemRefunded, bulkMarkPaymentItemsReturned, bulkMarkPaymentItemsRefunded,
  cancelPaymentItem,
} from "@/lib/mock-data";
import { downloadPaymentRunCsv, downloadPaymentRunNacha } from "@/lib/payment-exports";
import { useStoreVersion } from "@/hooks/useStore";
import { Gated } from "@/components/Gated";
import { usePersistedState } from "@/lib/demoState";
import { toast } from "sonner";

// ADM-10 — Payment run detail + export
export default function PaymentRunDetailPage() {
  // Subscribe this page to global store mutations. AdminShell's own
  // useStoreVersion() only re-renders the shell — react-router-dom's <Outlet/>
  // route child does NOT automatically re-render with the layout. Without this
  // hook, per-row mutations like markPaymentItemStatus(...) flip localStorage
  // but the table stays visually stale.
  useStoreVersion();
  const { runId } = useParams();
  const run = getPaymentRunById(runId) || ADM10_DETAIL_RUN;
  const generator = getAdminById(run.generatedBy);
  // Recompute on every render — AdminShell's useStoreVersion() forces a re-render
  // after every mutate(), and filtering a tiny array is cheap. A useMemo with
  // narrow deps caused per-row Mark sent / Mark completed UI to stay stale.
  const items = paymentItems.filter((p) => p.runId === run.id);
  const [decryptOpen, setDecryptOpen] = useState(false);
  const [decryptKind, setDecryptKind] = useState("nacha"); // nacha | csv-with-bank
  const [revealedItems, setRevealedItems] = usePersistedState(`run:${run.id}:revealed`, {}); // map<id, true>

  const totalAmount = items.reduce((a, x) => a + x.amount, 0);
  const largest = items.reduce((m, x) => (x.amount > (m?.amount || 0) ? x : m), null);
  const largestMember = largest ? getMemberById(largest.memberId) : null;
  const sentCount = items.filter((x) => x.status === "Sent" || x.status === "Completed").length;
  const returnedItems = items.filter((x) => x.status === "Returned");
  const refundedItems = items.filter((x) => x.status === "Refunded");

  const openDecrypt = (kind) => { setDecryptKind(kind); setDecryptOpen(true); };
  const confirmDecrypt = () => {
    setDecryptOpen(false);
    const newReveal = items.reduce((acc, x) => { acc[x.id] = true; return acc; }, {});
    setRevealedItems(newReveal);
    markPaymentRunDecrypted(run.id);

    // Trigger the actual file download (Blob → anchor click).
    const lookups = { getMemberById, getDealById, getBrandById };
    try {
      const result = decryptKind === "nacha"
        ? downloadPaymentRunNacha(run, items, lookups)
        : downloadPaymentRunCsv(run, items, lookups);

      // For NACHA export, also mark the run sent (transitions items Scheduled → Sent globally)
      if (decryptKind === "nacha" && run.status === "Generated") {
        markPaymentRunSent(run.id);
      }

      toast.success(`Downloaded ${result.filename}`, {
        description: `Decrypted ${items.length} bank accounts · audit log entry created.`,
      });
    } catch (err) {
      toast.error("Download failed", { description: err?.message || "Unable to generate export file." });
    }
  };

  // Mark a single payment item as Sent (Phase 1: manual flip + audit log).
  const handleMarkItemSent = (itemId) => {
    markPaymentItemStatus(itemId, "Sent", { sentAt: new Date().toISOString() });
    toast.success("Marked sent", { description: `Payment ${itemId} → Sent` });
  };

  // Mark a single payment item as Completed (settled).
  const handleMarkItemCompleted = (itemId) => {
    markPaymentItemStatus(itemId, "Completed", { completedAt: new Date().toISOString() });
    toast.success("Marked completed", { description: `Payment ${itemId} → Completed` });
  };

  // Mark every Sent item in this run as Completed.
  const handleMarkAllCompleted = () => {
    const sentItems = items.filter((i) => i.status === "Sent");
    if (sentItems.length === 0) {
      toast.info("Nothing to mark", { description: "No Sent items in this run." });
      return;
    }
    const flipped = markPaymentRunCompleted(run.id);
    toast.success(`Marked ${flipped} item${flipped === 1 ? "" : "s"} completed`, {
      description: `Run ${run.id} → settled in member bank accounts.`,
    });
  };

  // Mark all eligible/scheduled items in the run as Sent in one call.
  const handleMarkAllSent = () => {
    const pending = items.filter((i) => i.status === "Scheduled" || i.status === "Eligible");
    if (pending.length === 0) {
      toast.info("Nothing to mark", { description: "No scheduled or eligible items remain." });
      return;
    }
    markPaymentRunSent(run.id);
    toast.success(`Marked ${pending.length} item${pending.length === 1 ? "" : "s"} sent`, {
      description: `Run ${run.id} → Sent`,
    });
  };

  // ─── Manual return / refund (Phase 1 — pure status flip + audit log) ──────
  const [selected, setSelected] = useState({}); // map<itemId, true>
  const [actionDialog, setActionDialog] = useState(null); // { kind: 'returned'|'refunded', itemId?: string, bulk?: boolean }
  const [actionReason, setActionReason] = useState("");
  const [actionNote, setActionNote] = useState("");
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = items.length > 0 && selectedIds.length === items.length;

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(items.reduce((a, x) => { a[x.id] = true; return a; }, {}));
  };
  const toggleOne = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));

  const openAction = (kind, opts = {}) => {
    setActionReason(
      kind === "returned" ? "R03 — No Account / Unable to Locate Account"
        : kind === "refunded" ? "Member request"
        : "Cancelled before ACH submission");
    setActionNote("");
    setActionDialog({ kind, ...opts });
  };
  const confirmAction = () => {
    if (!actionDialog) return;
    const { kind, itemId, bulk } = actionDialog;
    const reason = actionReason.trim() || null;
    if (bulk) {
      const ids = selectedIds;
      if (kind === "returned") bulkMarkPaymentItemsReturned(ids, reason);
      else                     bulkMarkPaymentItemsRefunded(ids, reason);
      toast.success(`Marked ${ids.length} item${ids.length === 1 ? "" : "s"} ${kind}`, { description: reason });
      setSelected({});
    } else if (itemId) {
      if (kind === "returned")      markPaymentItemReturned(itemId, reason, actionNote.trim() || null);
      else if (kind === "refunded") markPaymentItemRefunded(itemId, reason);
      else if (kind === "cancelled") cancelPaymentItem(itemId, reason);
      toast.success(`Marked ${kind}`, { description: reason });
    }
    setActionDialog(null);
  };

  return (
    <div data-testid="payment-run-detail-page" className="space-y-5 pb-10">
      <Breadcrumbs items={[
        { label: "Admin", to: "/admin/dashboard" },
        { label: "Payment runs", to: "/admin/payments" },
        { label: run.id },
      ]} />

      {/* Header */}
      <div className="bg-surface border border-line rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-brand text-h1 text-ink font-semibold">Run {run.id}</h1>
              <StatusBadge entity="paymentRun" status={run.status} dot />
            </div>
            <div className="mt-1.5 text-caption text-ink-subtle">
              Generated {format.dateTime(run.generatedAt)} by {generator?.firstName} {generator?.lastName}
              {" "}· cutoff {format.date(run.cutoffDate)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Gated action="payments.export">
              <Button variant="outline" onClick={() => openDecrypt("csv")} data-testid="export-csv-btn">
                <FileSpreadsheet className="size-4 mr-1.5" /> Export CSV
              </Button>
            </Gated>
            <Gated action="payments.export">
              <Button variant="outline" onClick={() => openDecrypt("nacha")} className="border-brand-700 text-brand-800 hover:bg-brand-50" data-testid="export-nacha-btn">
                <FileDown className="size-4 mr-1.5" /> Export NACHA
              </Button>
            </Gated>
            <Gated action="payments.markSent">
              <Button className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="mark-all-sent-btn"
                      onClick={handleMarkAllSent}>
                <Send className="size-4 mr-1.5" /> Mark all sent
              </Button>
            </Gated>
            {items.some(i => i.status === "Sent") && (
              <Gated action="payments.markCompleted">
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mark-all-completed-btn"
                        onClick={handleMarkAllCompleted}>
                  <CheckCircle2 className="size-4 mr-1.5" /> Mark all completed
                </Button>
              </Gated>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Members" value={format.count(run.memberCount)} sub="receiving payouts" />
        <SummaryCard label="Items" value={format.count(items.length)} sub={`${sentCount} sent / ${items.length - sentCount} scheduled`} />
        <SummaryCard label="Total amount" value={format.currency(totalAmount)} accent />
        <SummaryCard label="Largest payout" value={largest ? format.currency(largest.amount) : "—"}
                     sub={largestMember ? `${largestMember.firstName} ${largestMember.lastName}` : ""} />
      </div>

      {/* Returned / refunded callout — surfaces R03 and similar ACH failures from completed runs (e.g. run_002) */}
      {(returnedItems.length > 0 || refundedItems.length > 0) && (
        <div data-testid="returned-callout" className="bg-red-50 border border-red-200 rounded-xl">
          <div className="p-5 flex items-start gap-3">
            <RefreshCcw className="size-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <h3 className="text-h3 text-ink font-semibold">
                  {returnedItems.length > 0 && <>{returnedItems.length} returned</>}
                  {returnedItems.length > 0 && refundedItems.length > 0 && <> · </>}
                  {refundedItems.length > 0 && <>{refundedItems.length} refunded</>}
                </h3>
                <span className="text-caption text-red-700 font-semibold">Needs follow-up</span>
              </div>
              <p className="mt-1 text-body-sm text-ink-muted">
                ACH returns require updating the member&rsquo;s bank record and re-issuing payment in the next run. Refunds are tracked separately.
                {run.notes && <> · <span className="italic">{run.notes}</span></>}
              </p>
            </div>
          </div>
          <div className="border-t border-red-200 bg-white/60 rounded-b-xl overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="text-left text-eyebrow uppercase text-red-900/70 border-b border-red-200">
                  <th className="px-5 py-2.5">Payment</th>
                  <th className="px-3 py-2.5">Member</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Reason</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-5 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {[...returnedItems, ...refundedItems].map((p) => {
                  const mem = getMemberById(p.memberId);
                  return (
                    <tr key={p.id}>
                      <td className="px-5 py-2.5 font-mono text-caption text-ink-muted">{p.id}</td>
                      <td className="px-3 py-2.5">
                        {mem ? (
                          <Link to={`/admin/members/${mem.id}`} className="text-brand-700 hover:underline">
                            {mem.firstName} {mem.lastName}
                          </Link>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge entity="paymentItem" status={p.status} /></td>
                      <td className="px-3 py-2.5 text-ink">{p.returnReason || (p.status === "Refunded" ? "Member request" : "—")}</td>
                      <td className="px-3 py-2.5 text-ink-muted">{p.returnedAt ? format.date(p.returnedAt) : p.completedAt ? format.date(p.completedAt) : "—"}</td>
                      <td className="px-5 py-2.5 text-ink tabular-nums text-right">{format.currency(p.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Items table */}
      <div className="bg-surface border border-line rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-h3 text-ink font-semibold">Payment items</h2>
            <p className="text-caption text-ink-subtle mt-0.5">Each row is one ACH credit destined for a member&rsquo;s bank account on file.</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <>
                <span data-testid="bulk-selected-count" className="text-caption text-ink-muted mr-1">{selectedIds.length} selected</span>
                <Gated action="payments.markReturned">
                  <Button data-testid="bulk-mark-returned-btn" variant="outline" size="sm"
                          onClick={() => openAction("returned", { bulk: true })}
                          className="border-red-200 text-red-700 hover:bg-red-50">
                    <RefreshCcw className="size-3.5 mr-1.5" /> Mark returned
                  </Button>
                </Gated>
                <Gated action="payments.markRefunded">
                  <Button data-testid="bulk-mark-refunded-btn" variant="outline" size="sm"
                          onClick={() => openAction("refunded", { bulk: true })}
                          className="border-amber-200 text-amber-700 hover:bg-amber-50">
                    <RefreshCcw className="size-3.5 mr-1.5" /> Mark refunded
                  </Button>
                </Gated>
              </>
            )}
            {Object.keys(revealedItems).length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-caption text-orange-700 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-200">
                <Lock className="size-3" /> Bank accounts decrypted in this session
              </span>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-6 py-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-select-all" className="size-4 rounded border-line text-brand-700 focus:ring-brand-700" />
                </th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Member</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Deal</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Bank account</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Amount</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Ref</th>
                <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {items.map((item) => {
                const m = getMemberById(item.memberId);
                const deal = getDealById(item.dealId);
                const brand = deal ? getBrandById(deal.brandId) : null;
                const revealed = revealedItems[item.id];
                const canMarkSent = item.status === "Scheduled" || item.status === "Eligible";
                const canMarkCompleted = item.status === "Sent";
                const canReturn = item.status === "Sent" || item.status === "Completed" || item.status === "Scheduled";
                const canCancel = item.status === "Scheduled" || item.status === "Eligible";
                const isTerminal = item.status === "Returned" || item.status === "Refunded" || item.status === "Cancelled";
                return (
                  <tr key={item.id} className={`hover:bg-surface-alt/40 ${selected[item.id] ? "bg-brand-50/30" : ""}`}>
                    <td className="px-6 py-3">
                      <input type="checkbox" checked={!!selected[item.id]} onChange={() => toggleOne(item.id)}
                             data-testid={`item-select-${item.id}`}
                             className="size-4 rounded border-line text-brand-700 focus:ring-brand-700" />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <Link to={`/admin/members/${m?.id}`} className="text-ink font-medium hover:text-brand-700">{m?.firstName} {m?.lastName}</Link>
                      <div className="text-caption text-ink-subtle font-mono">{m?.id}</div>
                    </td>
                    <td className="px-3 py-3 text-ink-muted">{brand?.name || "—"}</td>
                    <td className="px-3 py-3 font-mono text-code">
                      {revealed
                        ? <span className="text-orange-700">021•••322 / {String(item.bankLast4).padStart(4,"0")}</span>
                        : <span className="text-ink-muted">{format.bankLast4(item.bankLast4)}</span>}
                    </td>
                    <td className="px-3 py-3 text-ink font-semibold tabular-nums text-right">{format.currency(item.amount)}</td>
                    <td className="px-3 py-3">
                      <StatusBadge entity="payment" status={item.status} />
                      {item.returnReason && (
                        <div className="text-[10px] text-red-700 mt-0.5 max-w-[180px] truncate" title={item.returnReason}>{item.returnReason}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-code text-ink-subtle">{item.refNumber || "—"}</td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      {isTerminal ? (
                        <span className="text-caption text-ink-hint">—</span>
                      ) : (
                        <div className="inline-flex items-center gap-3">
                          {canMarkSent && (
                            <Gated action="payments.markSent">
                              <button data-testid={`mark-sent-${item.id}`}
                                      onClick={() => handleMarkItemSent(item.id)}
                                      className="text-caption font-semibold text-brand-700 hover:text-brand-800">
                                Mark sent
                              </button>
                            </Gated>
                          )}
                          {canMarkCompleted && (
                            <Gated action="payments.markCompleted">
                              <button data-testid={`mark-completed-${item.id}`}
                                      onClick={() => handleMarkItemCompleted(item.id)}
                                      className="text-caption font-semibold text-emerald-700 hover:text-emerald-800">
                                Mark completed
                              </button>
                            </Gated>
                          )}
                          {canReturn && (
                            <Gated action="payments.markReturned">
                              <button data-testid={`mark-returned-${item.id}`}
                                      onClick={() => openAction("returned", { itemId: item.id })}
                                      className="text-caption font-semibold text-red-700 hover:text-red-800">
                                Mark returned
                              </button>
                            </Gated>
                          )}
                          {canReturn && (
                            <Gated action="payments.markRefunded">
                              <button data-testid={`mark-refunded-${item.id}`}
                                      onClick={() => openAction("refunded", { itemId: item.id })}
                                      className="text-caption font-semibold text-amber-700 hover:text-amber-800">
                                Refund
                              </button>
                            </Gated>
                          )}
                          {canCancel && (
                            <Gated action="payments.cancel">
                              <button data-testid={`cancel-payment-${item.id}`}
                                      onClick={() => openAction("cancelled", { itemId: item.id })}
                                      className="text-caption font-semibold text-ink-muted hover:text-ink">
                                Cancel
                              </button>
                            </Gated>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-surface-alt">
              <tr className="text-body-sm">
                <td className="px-6 py-3.5 text-eyebrow uppercase text-ink-hint" colSpan={4}>Total</td>
                <td className="px-3 py-3.5 text-ink font-bold tabular-nums text-right">{format.currency(totalAmount)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Manual return / refund / cancel dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent data-testid="return-refund-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-h2">
              {actionDialog?.kind === "returned" ? "Mark returned"
                : actionDialog?.kind === "refunded" ? "Mark refunded"
                : "Cancel payment"}
              {actionDialog?.bulk && ` (${selectedIds.length})`}
            </DialogTitle>
            <DialogDescription className="leading-relaxed pt-1">
              {actionDialog?.kind === "returned"
                ? "Record the ACH return code and any context. This is a manual status change — no funds move."
                : actionDialog?.kind === "refunded"
                ? "Record why this payment is being refunded. This is a manual status change — no funds move."
                : "Cancel a scheduled payment before it ships. Funds were never released. The member will see this as Cancelled."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-2">
            <div>
              <label className="text-caption font-semibold text-ink mb-1.5 block">
                {actionDialog?.kind === "returned" ? "Return code / reason"
                  : actionDialog?.kind === "refunded" ? "Refund reason"
                  : "Cancellation reason"}
              </label>
              <input value={actionReason} onChange={(e) => setActionReason(e.target.value)}
                     data-testid="action-reason-input"
                     className="w-full h-9 px-3 border border-line rounded-md text-body-sm bg-surface focus:border-brand-700 focus:ring-2 focus:ring-brand-100 outline-none" />
              {actionDialog?.kind === "returned" && (
                <div className="mt-2 text-[11px] text-ink-subtle">
                  Common codes: R01 (Insufficient funds), R02 (Account closed), R03 (No account), R04 (Invalid account)
                </div>
              )}
            </div>
            {actionDialog?.kind === "returned" && !actionDialog?.bulk && (
              <div>
                <label className="text-caption font-semibold text-ink mb-1.5 block">Note (optional)</label>
                <textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)}
                          rows={2}
                          data-testid="action-note-input"
                          className="w-full px-3 py-2 border border-line rounded-md text-body-sm bg-surface focus:border-brand-700 focus:ring-2 focus:ring-brand-100 outline-none resize-none" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={confirmAction} data-testid="confirm-action-btn"
                    className={actionDialog?.kind === "returned" ? "bg-red-600 hover:bg-red-700 text-white"
                              : actionDialog?.kind === "refunded" ? "bg-amber-600 hover:bg-amber-700 text-white"
                              : "bg-ink hover:bg-ink/90 text-white"}>
              Confirm {actionDialog?.kind === "returned" ? "return"
                       : actionDialog?.kind === "refunded" ? "refund"
                       : "cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decryption confirmation modal — the critical moment */}
      <Dialog open={decryptOpen} onOpenChange={setDecryptOpen}>
        <DialogContent className="max-w-lg" data-testid="decrypt-dialog">
          <DialogHeader>
            <div className="size-10 rounded-md bg-orange-50 text-orange-600 grid place-items-center mb-3">
              <ShieldAlert className="size-5" />
            </div>
            <DialogTitle className="font-brand text-h2">Decrypt {items.length} bank accounts?</DialogTitle>
            <DialogDescription className="leading-relaxed pt-1">
              You&rsquo;re about to decrypt encrypted bank account numbers to generate the {decryptKind === "nacha" ? "NACHA" : "CSV"} export.
              This is a sensitive action and will be logged to the audit log with your admin ID, IP, and timestamp.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-surface-alt border border-line rounded-md p-4 my-2 space-y-2.5">
            <div className="eyebrow">Audit-log preview</div>
            <div className="font-mono text-code text-ink-muted leading-relaxed">
              <div><span className="text-ink-hint">[{format.dateTime(new Date())}]</span></div>
              <div><span className="text-orange-700 font-semibold">PaymentRun.decryptBankAccounts</span></div>
              <div>actor: <span className="text-ink">{generator?.email || "current admin"}</span></div>
              <div>run: <span className="text-ink">{run.id}</span> · count: {items.length} · ip: 207.158.42.18</div>
            </div>
          </div>

          <ul className="space-y-2 text-caption text-ink-muted">
            <li className="flex gap-2"><CheckCircle2 className="size-3.5 text-emerald-600 mt-0.5 shrink-0" /> Only Payments Overseer or Super Admin can perform this action.</li>
            <li className="flex gap-2"><CheckCircle2 className="size-3.5 text-emerald-600 mt-0.5 shrink-0" /> Decrypted values are held in memory for this session only — never persisted.</li>
            <li className="flex gap-2"><AlertCircle className="size-3.5 text-orange-600 mt-0.5 shrink-0" /> Re-decryption will create a new audit entry.</li>
          </ul>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDecryptOpen(false)}>Cancel</Button>
            <Button onClick={confirmDecrypt} className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="confirm-decrypt-btn">
              <Lock className="size-4 mr-1.5" /> Decrypt and download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, sub, accent = false }) {
  return (
    <div className="bg-surface border border-line rounded-xl p-5">
      <div className="text-caption text-ink-subtle">{label}</div>
      <div className={`mt-1 font-brand text-[28px] font-bold leading-none tabular-nums ${accent ? "text-brand-700" : "text-ink"}`}>{value}</div>
      {sub && <div className="mt-2 text-caption text-ink-subtle truncate">{sub}</div>}
    </div>
  );
}
